/**
 * Single source of truth for the public routes' SEO meta.
 * Used by pages (via usePageMeta) and by the build-time prerender step in
 * vite.config.ts, which writes per-route static HTML so social crawlers that
 * don't run JavaScript still see the right tags.
 *
 * Keep this file free of runtime-only imports — vite.config.ts imports it in
 * Node at build time.
 */

export interface RouteMeta {
  /** Excluded from sitemap when set, even if a snapshot exists. */
  noindex?: boolean;
  /** Page title (without site name suffix — appended automatically). */
  title: string;
  /** Meta description, ~150-160 chars. */
  description: string;
  /** Route path, e.g. "/about". Used for canonical + og:url. */
  path: string;
  /** Crawler-visible page heading used by the static snapshot. */
  heading: string;
  /** Concise primary copy for the static snapshot. */
  summary: string;
  /** Optional additional crawler-visible page copy. */
  details?: string[];
}

export const SITE_NAME = "Reframe Youth";

export const ROUTE_META: Record<string, RouteMeta> = {
  "/": {
    title: "Youth Ministry Curriculum That Sparks Real Conversations",
    description:
      "Conversation-driven youth ministry curriculum where no topic is off-limits. Relevant to culture, faithful to Scripture, built by leaders in the trenches.",
    path: "/",
    heading: "Youth Ministry Curriculum That Sparks Real Conversations",
    summary:
      "Reframe Youth creates conversation-driven curriculum that helps youth leaders address faith, culture, mental health, identity, and the questions students are already asking.",
  },
  "/about": {
    title: "About: Bridging Faith and Culture in Youth Ministry",
    description:
      "Meet the team behind Reframe Youth: a licensed counselor and youth pastors creating curriculum that helps students navigate culture without losing their faith.",
    path: "/about",
    heading: "Built by Youth Leaders, for Youth Leaders",
    summary:
      "Meet the counselors and youth pastors behind Reframe Youth and learn why we build biblically grounded resources for ministry in the real world.",
  },
  "/training": {
    title: "The Art of Conversation Training",
    description:
      "A free conversation training for youth leaders and parents. Ten short videos, real stories from ministry and parenting, built by leaders in the trenches.",
    path: "/training",
    heading: "The Art of Conversation",
    summary:
      "A leader and parent training on how to have better conversations with the young people in your life.",
  },
  "/tools": {
    title: "Free Youth Ministry Tools for Leaders",
    description:
      "Free, research-grounded tools for youth leaders: private assessments, ministry surveys, curriculum recommendations, and conversation training.",
    path: "/tools",
    heading: "Free Youth Ministry Tools",
    summary:
      "Explore practical, research-grounded tools for youth leaders, including ministry health surveys, curriculum recommendations, and leader wellbeing resources.",
  },
  "/self-assessment": {
    title: "Free Mental Health Self-Assessment for Youth Leaders",
    description:
      "Take a free, private mental health self-assessment for youth leaders using clinically validated screenings for anxiety, depression, trauma, and dissociation.",
    path: "/self-assessment",
    heading: "A Private Mental Health Check-In for Youth Leaders",
    summary:
      "Use four clinically validated screening tools to reflect on anxiety, depression, childhood experiences, and dissociation. The assessment is free, private, and takes about 15 minutes.",
  },
  "/build-bundle": {
    title: "Build Your Curriculum Bundle: Save on Series",
    description:
      "Mix and match youth ministry series to build a custom curriculum bundle. Pick the topics your students need and save when you bundle multiple series.",
    path: "/build-bundle",
    heading: "Build Your Youth Ministry Curriculum Bundle",
    summary:
      "Choose the youth ministry series your students need, combine them into a custom curriculum bundle, and save as you add more series.",
  },
  "/curriculum-quiz": {
    title: "Curriculum Quiz: Find the Right Series for Your Group",
    description:
      "Answer a few quick questions about your youth group and get a personalized youth ministry curriculum recommendation matched to your students' needs.",
    path: "/curriculum-quiz",
    heading: "Find the Right Curriculum for Your Youth Group",
    summary:
      "Answer a few questions about your students and ministry context to get a curriculum recommendation matched to your group.",
  },
  "/ministry-survey": {
    title: "Ministry Health Survey: Hear From Your Students",
    description:
      "An anonymous student survey that measures the health of your youth ministry across safety, trust, belonging, and more, with a clear dashboard for leaders.",
    path: "/ministry-survey",
    heading: "Hear What Your Students Really Think",
    summary:
      "Use an anonymous ministry health survey to understand safety, trust, belonging, spiritual growth, and the experiences shaping your youth group.",
  },
  "/shop": {
    title: "Youth Ministry Curriculum and Teaching Resources",
    description:
      "Shop conversation-driven youth ministry curriculum, complete teaching toolkits, and full-year discipleship resources for groups of every size.",
    path: "/shop",
    heading: "Youth Ministry Curriculum Built for Real Conversations",
    summary:
      "Browse complete curriculum volumes and discipleship toolkits with teaching guides, small-group resources, media, parent resources, and leader training.",
  },
  "/wld": {
    title: "Whole Life Discipleship Volume 3",
    description:
      "Whole Life Discipleship Volume 3 is a full-year high school curriculum that pairs seven Core Values with the seven Signs of a Healthy Youth Ministry, built on 2025 research with 424 urban teens and 128 urban leaders.",
    path: "/wld",
    heading: "Your students are showing up. Are they being formed?",
    summary:
      "Whole Life Discipleship Volume 3 is a full-year high school curriculum built on a conviction we can no longer treat as optional: disciples are not produced by content alone. They are built in a culture.",
    details: [
      "Presale now open. Volume 3 releases January 1, 2027.",
    ],
  },
  "/blog": {
    title: "Truth in the Trenches: Youth Ministry Blog",
    description:
      "Practical, biblically grounded articles for youth pastors about discipleship, culture, leadership, student wellbeing, and life in ministry.",
    path: "/blog",
    heading: "Truth in the Trenches",
    summary:
      "Practical, biblically grounded help for the real trenches of youth ministry, discipleship, culture, leadership, and everything in between.",
  },
  "/statement-of-faith": {
    title: "Statement of Faith",
    description:
      "Read Reframe Youth's statement of faith on Scripture, the Trinity, Jesus Christ, the Holy Spirit, salvation, the Church, and Christian discipleship.",
    path: "/statement-of-faith",
    heading: "Our Statement of Faith",
    summary:
      "Reframe Youth holds to historic, orthodox Christian beliefs that shape our curriculum, ministry resources, and approach to discipleship.",
    details: [
      "We affirm the Bible as the complete and divinely inspired Word of God, and believe in one God eternally existing as Father, Son, and Holy Spirit.",
      "We believe Jesus Christ is fully God and fully man, lived without sin, died for our sins, rose bodily, ascended into heaven, and will come again.",
      "We believe the Holy Spirit is God and presently convicts the world, indwells believers, seals them, and empowers the Church with spiritual gifts.",
      "We believe humanity was created in God's image, fell into sin, and cannot save itself from spiritual and physical death.",
      "We believe salvation is God's free gift of grace, received through faith in the death and resurrection of Jesus Christ rather than human merit.",
      "We believe every believer is called to sanctification, can rest in the assurance of salvation, and belongs to the body of Christ expressed through local churches.",
      "We believe Christians are called to lives of righteousness, the fruit and fullness of the Spirit, godly relationships, and the Great Commission.",
    ],
  },
  "/first-aid-kit": {
    title: "Free First Aid Kit for Youth Leaders",
    description:
      "Use two free youth ministry tools: a private leader mental health self-assessment and an anonymous student ministry health survey.",
    path: "/first-aid-kit",
    heading: "A First Aid Kit for Youth Leaders",
    summary:
      "You pour into everyone else. These free tools help you care for your own wellbeing and understand what students are experiencing in your ministry.",
    details: [
      "Take a private mental health self-assessment designed for the realities of youth ministry leadership.",
      "Create an anonymous student survey to learn how your group experiences safety, trust, belonging, and spiritual growth.",
    ],
  },
};
